import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLocations } from "@/hooks/useLocations";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import AreaField from "@/components/common/AreaField";
import RichTextEditor from "@/components/common/RichTextEditor";
import CategoryBreadcrumbChip from "@/components/common/CategoryBreadcrumbChip";
import { Loader2, Plus, Trash2, Image as ImageIcon, CalendarClock, Wand2 } from "lucide-react";

const MAX_THUMB_BYTES = 3 * 1024 * 1024; // 3 MB per spec
const THUMB_MIME_RE = /^image\/(png|jpe?g|gif)$/i;

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const EMPTY_LOCATION = {
  title: "",
  content: "",
  date: "",
  time: "",
  address: "",
  booking_url: "",
};

/**
 * Post Your Event — legacy-parity submission form.
 * Spec: `GET /api/agent-docs/WEB_MOBILE_SPEC_POST_EVENT`
 *
 * Client-side guards (avoid known 422s):
 *   • Thumb image is REQUIRED — the file input is validated before submit
 *     and the "Submit" button stays disabled until an image_url is set.
 *   • locations must be non-empty (min 1). "+ Add another location" appends
 *     a blank row; "Remove" is disabled when only one row remains.
 *
 * After submit: shows the backend's pending message + link to My events.
 */
export default function PostEventPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: regions = [] } = useLocations();
  const [searchParams] = useSearchParams();

  // Chooser (iter-216) forwards the chosen category/subcategory ids as query
  // params so we can echo them on submit — the backend uses them to attach
  // the event to the right node in the taxonomy.
  const categoryId = searchParams.get("category_id") || null;
  const subcategoryId = searchParams.get("subcategory_id") || null;

  const [form, setForm] = useState({
    title: "",
    description: "",
    description_html: "",
    contact_email: user?.email || "",
    contact_phone: "",
    image_url: "",
    country: "NZ",
    region: "",
    city: "",
    area: "",
    pincode: "",
    address: "",
    locations: [{ ...EMPTY_LOCATION }],
  });
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {id, message} once accepted
  const [extractMode, setExtractMode] = useState("url"); // "url" | "text"
  const [extractUrl, setExtractUrl] = useState("");
  const [extractText, setExtractText] = useState("");

  const cities = useMemo(
    () => regions.find((r) => r.region === form.region)?.cities || [],
    [regions, form.region],
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLoc = (i, patch) =>
    setForm((f) => ({
      ...f,
      locations: f.locations.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
    }));
  const addLoc = () =>
    setForm((f) => ({ ...f, locations: [...f.locations, { ...EMPTY_LOCATION }] }));
  const removeLoc = (i) =>
    setForm((f) => ({
      ...f,
      locations: f.locations.length > 1 ? f.locations.filter((_, idx) => idx !== i) : f.locations,
    }));

  const uploadThumb = async (file) => {
    // Guard: type + size BEFORE hitting the API to avoid a 422 round-trip.
    if (!THUMB_MIME_RE.test(file.type)) {
      toast.error("Thumb must be a PNG, JPG, or GIF.");
      return;
    }
    if (file.size > MAX_THUMB_BYTES) {
      toast.error("Thumb must be ≤ 3 MB.");
      return;
    }
    setUploading(true);
    try {
      const dataUri = await readAsDataURL(file);
      const { data } = await sharedApi.post("/uploads/image", { data: dataUri });
      if (data.url) set({ image_url: data.url });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const mut = useMutation({
    mutationFn: async () => {
      const body = { ...form };
      // Trim empty booking_urls so the backend doesn't reject a "" as a bad URL.
      body.locations = body.locations.map((l) => ({
        ...l,
        booking_url: l.booking_url?.trim() || undefined,
      }));
      // If the rich-text editor produced only an empty-ish shell, drop the
      // html payload so the sanitizer doesn't return a "<br>" body.
      const stripped = (body.description_html || "").replace(/<[^>]+>/g, "").trim();
      if (!stripped) body.description_html = undefined;
      // Iter-216: attach chosen category/subcategory ids when the chooser
      // provided them. Backend defaults to the Create Event root if omitted.
      if (categoryId) body.category_id = categoryId;
      if (subcategoryId) body.subcategory_id = subcategoryId;
      const { data } = await sharedApi.post("/classifieds/events/submit", body);
      return data;
    },
    onSuccess: (data) => {
      setSubmitted({ id: data.id, message: data.message });
      toast.success(data.message || "Thanks! Your event is under review.");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Couldn't submit event");
    },
  });

  // Iter-217 AI auto-fill: the shared LLM extractor parses either a URL or a
  // pasted blob (event page HTML, poster copy, etc.) into the full submit
  // schema — title, description(_html), image, locations[], contact fields.
  // The scrape/LLM path can take 20–60 s, hence the generous timeout.
  const extractMut = useMutation({
    mutationFn: async () => {
      const body = extractMode === "url"
        ? { url: extractUrl.trim() }
        : { text: extractText.trim() };
      const { data } = await sharedApi.post(
        "/classifieds/events/extract-from-url",
        body,
        { timeout: 90_000 },
      );
      return data;
    },
    onSuccess: (data) => {
      const e = data?.event || data || {};
      const imgs = Array.isArray(data?.images) ? data.images : (e.image_url ? [e.image_url] : []);
      const locations = Array.isArray(e.locations) && e.locations.length
        ? e.locations.map((l) => ({
            title: l.title || "",
            content: l.content || "",
            date: l.date || "",
            time: l.time || "",
            address: l.address || "",
            booking_url: l.booking_url || "",
          }))
        : form.locations;
      setForm((f) => ({
        ...f,
        title: e.title || f.title,
        description: e.description || f.description,
        description_html: e.description_html || f.description_html,
        contact_email: e.contact_email || f.contact_email,
        contact_phone: e.contact_phone || f.contact_phone,
        image_url: imgs[0] || f.image_url,
        country: e.country || f.country,
        region: e.region || f.region,
        city: e.city || f.city,
        area: e.area || f.area,
        pincode: e.pincode || f.pincode,
        address: e.address || f.address,
        locations,
      }));
      toast.success("Auto-filled — review the details, then submit.");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Couldn't extract event details — try paste-text mode.");
    },
  });

  const canSubmit =
    !!form.title.trim() &&
    !!form.image_url &&
    !!form.contact_email.trim() &&
    form.locations.length >= 1 &&
    form.locations.every(
      (l) => l.title.trim() && l.content.trim() && l.date.trim() && l.time.trim() && l.address.trim(),
    );

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error("Please add a thumb image first.");
      return;
    }
    mut.mutate();
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="post-event-success">
        <Seo title="Event submitted" />
        <div className="mx-auto w-16 h-16 rounded-full grid place-items-center bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)]">
          <CalendarClock className="w-8 h-8" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-black text-slate-900">
          Thanks — your event is under review
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          {submitted.message ||
            "We'll notify you as soon as our team reviews your submission (usually within 24 hours)."}
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link
            to="/my/events"
            data-testid="post-event-success-my-events"
            className="rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5"
          >
            Go to My events
          </Link>
          <Link
            to="/events"
            className="rounded-full border border-soft text-slate-700 hover:border-[color:var(--brand-blue)] font-semibold px-5 py-2.5"
          >
            Back to events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="post-event-page">
      <Seo title="Post your event" description="Submit your event for review — approved events appear on Allsale within 24 hours." />
      <Breadcrumbs items={[{ label: "Events", to: "/events" }, { label: "Post your event" }]} />

      <h1 className="mt-6 font-display text-3xl md:text-4xl font-black text-slate-900">
        Post your event
      </h1>
      <CategoryBreadcrumbChip
        categoryId={categoryId}
        subcategoryId={subcategoryId}
        fallbackLabel="Create Event"
        testId="post-event-change-category"
      />
      <p className="mt-2 text-slate-500 leading-relaxed">
        Approved events appear on the Events page and reach thousands of Kiwis. Review usually within 24 hours.
      </p>

      {/* Iter-217 auto-fill box — URL or paste-text → fills every field */}
      <div
        className="mt-6 rounded-2xl border border-[color:var(--brand-blue)]/25 bg-[color:var(--brand-blue-wash)]/60 p-4 md:p-5"
        data-testid="post-event-extract-card"
      >
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <Wand2 className="w-4 h-4 text-[color:var(--brand-blue)]" />
          Have a link or a poster? Auto-fill from a URL or pasted copy
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Paste an event page URL (or the raw text from a flyer / social post) — we&apos;ll extract title, description, sessions, and image. Usually 20–60 seconds.
        </p>

        <div className="mt-3 inline-flex rounded-full bg-white border border-soft p-0.5 text-xs font-bold">
          <button
            type="button"
            onClick={() => setExtractMode("url")}
            data-testid="post-event-extract-mode-url"
            className={`px-3 py-1.5 rounded-full transition-colors ${
              extractMode === "url"
                ? "bg-[color:var(--brand-blue)] text-white"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            From URL
          </button>
          <button
            type="button"
            onClick={() => setExtractMode("text")}
            data-testid="post-event-extract-mode-text"
            className={`px-3 py-1.5 rounded-full transition-colors ${
              extractMode === "text"
                ? "bg-[color:var(--brand-blue)] text-white"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Paste text
          </button>
        </div>

        <div className="mt-3 flex items-start gap-2 flex-col sm:flex-row">
          {extractMode === "url" ? (
            <input
              data-testid="post-event-extract-url"
              className="input flex-1"
              type="url"
              value={extractUrl}
              onChange={(e) => setExtractUrl(e.target.value)}
              placeholder="https://…"
            />
          ) : (
            <textarea
              data-testid="post-event-extract-text"
              className="input min-h-[6rem] flex-1"
              value={extractText}
              onChange={(e) => setExtractText(e.target.value)}
              placeholder="Paste the event description, poster copy, or social post text here…"
              maxLength={20000}
            />
          )}
          <button
            type="button"
            data-testid="post-event-extract-btn"
            onClick={() => extractMut.mutate()}
            disabled={
              extractMut.isPending ||
              (extractMode === "url" ? !extractUrl.trim() : !extractText.trim())
            }
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-40 text-white font-bold px-5 py-2.5 whitespace-nowrap"
          >
            {extractMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {extractMut.isPending ? "Extracting…" : "Auto-fill"}
          </button>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-8 space-y-6">
        <Field label="Event title" required>
          <input
            data-testid="post-event-title"
            className="input"
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="e.g. Diwali Night Market"
            maxLength={200}
            required
          />
        </Field>

        <Field label="Event description" hint="Basic formatting: bold · italic · underline · strike · size · text colour · highlight">
          <RichTextEditor
            testId="post-event-description"
            value={form.description_html}
            onChange={(html) => set({ description_html: html })}
            onPlainChange={(text) => set({ description: text })}
            placeholder="Tell attendees what to expect"
            maxLength={60000}
          />
          <div className="mt-1 text-xs text-slate-400">
            {form.description.length}/60000
          </div>
        </Field>

        {/* Thumb image */}
        <Field label="Thumb image" required hint="PNG, JPG or GIF · max 3 MB">
          {form.image_url ? (
            <div className="flex items-center gap-4">
              <img
                src={form.image_url}
                alt="Thumb preview"
                className="w-32 h-24 rounded-lg object-cover border border-soft"
              />
              <button
                type="button"
                onClick={() => set({ image_url: "" })}
                data-testid="post-event-thumb-remove"
                className="text-sm font-semibold text-red-600 hover:underline"
              >
                Remove
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-soft hover:border-[color:var(--brand-blue)] cursor-pointer px-4 py-4 transition-colors">
              <ImageIcon className="w-5 h-5 text-slate-400" />
              <span className="text-sm font-semibold text-slate-600">
                {uploading ? "Uploading…" : "Click to upload thumb image"}
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif"
                className="hidden"
                data-testid="post-event-thumb-input"
                onChange={(e) => e.target.files?.[0] && uploadThumb(e.target.files[0])}
              />
            </label>
          )}
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Contact email" required>
            <input
              type="email"
              data-testid="post-event-email"
              className="input"
              value={form.contact_email}
              onChange={(e) => set({ contact_email: e.target.value })}
              required
            />
          </Field>
          <Field label="Contact phone">
            <input
              data-testid="post-event-phone"
              className="input"
              value={form.contact_phone}
              onChange={(e) => set({ contact_phone: e.target.value })}
            />
          </Field>
        </div>

        {/* Locations */}
        <div className="pt-4 border-t border-subtle">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900">
              Event sessions
            </h2>
            <span className="text-xs text-slate-500">Min 1 required</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Add one row per session, venue, or date. Multi-day and multi-venue events are supported.
          </p>

          <div className="mt-4 space-y-4">
            {form.locations.map((loc, i) => (
              <div
                key={i}
                data-testid={`post-event-location-${i}`}
                className="rounded-2xl border border-soft bg-white p-4 md:p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-slate-700">
                    Session {i + 1}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLoc(i)}
                    disabled={form.locations.length <= 1}
                    data-testid={`post-event-location-remove-${i}`}
                    className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-30 disabled:pointer-events-none inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>

                <div className="mt-3 space-y-3">
                  <Field label="Session title" required compact>
                    <input
                      className="input"
                      value={loc.title}
                      onChange={(e) => setLoc(i, { title: e.target.value })}
                      placeholder="e.g. Main show"
                      required
                    />
                  </Field>
                  <Field label="Content" required compact hint="Short description shown on the session card">
                    <textarea
                      className="input min-h-[4rem]"
                      value={loc.content}
                      onChange={(e) => setLoc(i, { content: e.target.value })}
                      required
                    />
                  </Field>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Date" required compact hint="YYYY-MM-DD">
                      <input
                        type="date"
                        className="input"
                        value={loc.date}
                        onChange={(e) => setLoc(i, { date: e.target.value })}
                        required
                      />
                    </Field>
                    <Field label="Time" required compact hint='Free text — e.g. "7:30 PM"'>
                      <input
                        className="input"
                        value={loc.time}
                        onChange={(e) => setLoc(i, { time: e.target.value })}
                        placeholder="7:30 PM"
                        required
                      />
                    </Field>
                  </div>
                  <Field label="Address" required compact>
                    <input
                      className="input"
                      value={loc.address}
                      onChange={(e) => setLoc(i, { address: e.target.value })}
                      required
                    />
                  </Field>
                  <Field label="Booking URL (optional)" compact hint="Attendees will see a 'Book' button">
                    <input
                      type="url"
                      className="input"
                      value={loc.booking_url}
                      onChange={(e) => setLoc(i, { booking_url: e.target.value })}
                      placeholder="https://…"
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addLoc}
            data-testid="post-event-add-location"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-dashed border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] font-semibold px-4 py-2 hover:bg-[color:var(--brand-blue-wash)] transition-colors"
          >
            <Plus className="w-4 h-4" /> Add another location
          </button>
        </div>

        {/* Overall location */}
        <div className="pt-4 border-t border-subtle">
          <h2 className="font-display text-xl font-bold text-slate-900">Where</h2>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            <Field label="Region" compact>
              <select
                className="input"
                data-testid="post-event-region"
                value={form.region}
                onChange={(e) => set({ region: e.target.value, city: "", area: "" })}
              >
                <option value="">Select region</option>
                {regions.map((r) => (
                  <option key={r.region} value={r.region}>
                    {r.region}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="City" compact>
              <select
                className="input"
                data-testid="post-event-city"
                value={form.city}
                onChange={(e) => set({ city: e.target.value, area: "" })}
                disabled={!cities.length}
              >
                <option value="">Select city</option>
                {cities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Area / suburb" compact hint="Auto-suggests known suburbs · type manually if not listed">
              <AreaField
                testId="post-event-area"
                region={form.region}
                city={form.city}
                country={form.country}
                value={form.area}
                onChange={(v) => set({ area: v })}
              />
            </Field>
            <Field label="Pincode" compact>
              <input
                className="input"
                data-testid="post-event-pincode"
                value={form.pincode}
                onChange={(e) => set({ pincode: e.target.value })}
                maxLength={16}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Event address (overall)" compact>
              <input
                className="input"
                data-testid="post-event-address"
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
              />
            </Field>
          </div>
        </div>

        <div className="pt-4 border-t border-subtle flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Your event will be hidden until an admin approves it. We&apos;ll notify you either way.
          </p>
          <button
            type="submit"
            disabled={!canSubmit || mut.isPending || uploading}
            data-testid="post-event-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] disabled:opacity-40 disabled:pointer-events-none text-white font-bold px-6 py-3 transition-colors"
          >
            {mut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit for review
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, hint, compact, children }) {
  return (
    <label className="block">
      <span className={`block font-semibold text-slate-700 ${compact ? "text-sm mb-1" : "text-sm mb-1.5"}`}>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}
